import { Box, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import { useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import ExportButton from "../../../../../../components/buttons/ExportButton";
import SearchInput from "../../../../../../components/filters/SearchInput";
import StyledDataGrid from "../../../../../../components/StyledDataGrid";
import { useFileNaming } from "../../../../../../hooks/useFileNaming";
import { formatTimestampWithOptionalMinutes } from "../../../../../../utils/timestamp";
import IntegrationImage from "../../../../integrations/components/IntegrationImage";
import TextIntegrationStyle from "../../TextIntegrationStyle";
import { getServerLoginsRows } from "./checkRowsServerData";

const ServerLoginsView = ({ entityData }) => {
  const { trackEvent } = useAPI();
  const gridRef = useGridApiRef();
  const { generateBreezeFileName } = useFileNaming();
  const [appliedFilters, setAppliedFilters] = useState({
    searchString: "",
  });
  const allLogins = getServerLoginsRows(entityData);

  let filteredLogins = [];
  if (appliedFilters.searchString !== "") {
    const lowerCaseSearchString = appliedFilters.searchString.toLowerCase();
    filteredLogins = allLogins.filter(
      (row) =>
        row?.account_name?.toLowerCase().includes(lowerCaseSearchString) ||
        row?.login_status?.toLowerCase().includes(lowerCaseSearchString) ||
        row?.login_type?.toLowerCase().includes(lowerCaseSearchString) ||
        row?.is_admin?.toLowerCase().includes(lowerCaseSearchString)
    );
  } else filteredLogins = allLogins;

  const handleTableExport = () => {
    trackEvent(
      `User clicked export button for server logins - ${entityData?.display_name}`
    );
    const serverName =
      entityData?.display_name?.replace(/[^a-zA-Z0-9]/g, "_") || "server";
    const csvExportOptions = {
      fileName: generateBreezeFileName(`${serverName}_logins`),
    };
    gridRef.current?.exportDataAsCsv(csvExportOptions);
  };

  const columnsToShow = [
    {
      field: "source",
      headerName: "Source",
      flex: 0.5,
      renderCell: (params) => (
        <IntegrationImage
          name={params?.value}
          width={"24px"}
          style={{ margin: "16px" }}
          newLogo={true}
        />
      ),
    },
    {
      field: "account_name",
      headerName: "User",
      flex: 1,
      renderCell: (params) => (
        <TextIntegrationStyle
          title={""}
          value={params?.value || "Unknown User"}
        />
      ),
    },
    {
      field: "login_timestamp",
      headerName: "Seen At",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="caption">
          {formatTimestampWithOptionalMinutes(params?.value)}
        </Typography>
      ),
    },
    {
      field: "login_status",
      headerName: "Status",
      flex: 0.7,
      renderCell: (params) => (
        <Typography variant="caption">{params?.value}</Typography>
      ),
    },
    {
      field: "is_admin",
      headerName: "Admin",
      flex: 0.5,
      renderCell: (params) => (
        <Typography variant="caption">{params?.value}</Typography>
      ),
    },
  ];

  if (allLogins.length === 0) {
    return null;
  }

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2">
          Interactive Logins (Last 7 Days)
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ width: "300px" }}>
          <SearchInput
            fieldName="searchString"
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
          />
        </Box>
        <ExportButton
          handleExport={handleTableExport}
          disabled={!filteredLogins || filteredLogins.length === 0}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <StyledDataGrid
          apiRef={gridRef}
          columns={columnsToShow}
          rows={filteredLogins}
        />
      </Box>
    </>
  );
};

export default ServerLoginsView;
